import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes, faLightbulb, faSpinner } from '@fortawesome/free-solid-svg-icons';
import { SuggestionsAPI } from '../../api';

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.8);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  backdrop-filter: blur(5px);
`;

const ModalContainer = styled.div`
  background-color: var(--background-card);
  border-radius: 10px;
  width: 90%;
  max-width: 500px;
  max-height: 90vh; /* Limit height to 90% of viewport height */
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.3);
  position: relative;
  overflow: hidden;
  border: 1px solid var(--glass-border);
  animation: fadeIn 0.3s ease;
  
  @keyframes fadeIn {
    from { opacity: 0; transform: translateY(-20px); }
    to { opacity: 1; transform: translateY(0); }
  }
  
  @media (max-width: 480px) {
    width: 95%;
    max-height: 85vh; /* Slightly smaller on mobile */
    margin: 0 auto;
  }
`;

const CloseButton = styled.button`
  position: absolute;
  top: 15px;
  right: 15px;
  background: none;
  border: none;
  color: var(--text-dim);
  font-size: 20px;
  cursor: pointer;
  z-index: 10;
  
  &:hover {
    color: white;
  }
`;

const ModalHeader = styled.div`
  padding: 20px;
  text-align: center;
  border-bottom: 1px solid var(--glass-border);
  
  h2 {
    font-size: 24px;
    margin: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 10px;
    
    svg {
      color: var(--primary-light);
    }
  }
`;

const ModalBody = styled.div`
  padding: 20px;
  max-height: calc(90vh - 70px); /* Account for header height */
  overflow-y: auto;
  
  /* Scrollbar styling */
  &::-webkit-scrollbar {
    width: 6px;
  }
  
  &::-webkit-scrollbar-track {
    background: rgba(255, 255, 255, 0.05);
  }
  
  &::-webkit-scrollbar-thumb {
    background-color: rgba(255, 255, 255, 0.2);
    border-radius: 3px;
  }
  
  @media (max-width: 480px) {
    padding: 15px;
    max-height: calc(85vh - 60px); /* Adjust for smaller header on mobile */
    font-size: 0.95em;
  }
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 15px;
`;

const FormGroup = styled.div`
  position: relative;
`;

const Label = styled.label`
  display: block;
  margin-bottom: 8px;
  font-weight: 500;
`;

const Input = styled.input`
  width: 100%;
  padding: 12px 15px;
  border-radius: 5px;
  background-color: rgba(255, 255, 255, 0.1);
  border: 1px solid var(--glass-border);
  color: white;
  font-size: 16px;
  
  &:focus {
    outline: none;
    border-color: var(--primary-light);
    box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.3);
  }
  
  @media (max-width: 480px) {
    padding: 10px 12px;
    font-size: 14px;
  }
`;

const TextArea = styled.textarea`
  width: 100%;
  padding: 12px 15px;
  border-radius: 5px;
  background-color: rgba(255, 255, 255, 0.1);
  border: 1px solid var(--glass-border);
  color: white;
  font-size: 16px;
  min-height: 100px;
  resize: vertical;
  
  &:focus {
    outline: none;
    border-color: var(--primary-light);
    box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.3);
  }
  
  &::placeholder {
    color: var(--text-dim);
  }
  
  @media (max-width: 480px) {
    padding: 10px 12px;
    font-size: 14px;
    min-height: 80px;
  }
`;

const SubmitButton = styled.button`
  width: 100%;
  padding: 12px;
  background-color: var(--primary-color);
  color: white;
  border: none;
  border-radius: 5px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s;
  display: flex;
  align-items: center;
  justify-content: center;
  
  &:hover {
    background-color: var(--primary-light);
  }
  
  &:disabled {
    opacity: 0.7;
    cursor: not-allowed;
  }
  
  svg {
    margin-right: 8px;
  }
`;

const ErrorMessage = styled.div`
  color: var(--error-color);
  padding: 10px;
  background-color: rgba(229, 62, 62, 0.1);
  border-radius: 5px;
  margin-top: 15px;
  font-size: 14px;
`;

const SuccessMessage = styled.div`
  color: var(--success-color);
  padding: 10px;
  background-color: rgba(72, 187, 120, 0.1);
  border-radius: 5px;
  margin-top: 15px;
  font-size: 14px;
`;

const ContentInfo = styled.div`
  background-color: rgba(255, 255, 255, 0.05);
  padding: 10px;
  border-radius: 5px;
  margin-bottom: 15px;
  
  p {
    margin: 5px 0;
    font-size: 14px;
    color: var(--text-dim);
    
    strong {
      color: white;
    }
  }
  
  @media (max-width: 480px) {
    padding: 8px;
    margin-bottom: 10px;
    
    p {
      font-size: 13px;
      margin: 3px 0;
    }
  }
`;

const CurrentInfo = styled.div`
  background-color: rgba(255, 255, 255, 0.05);
  padding: 10px;
  border-radius: 5px;
  margin-bottom: 15px;
  
  h4 {
    margin: 0 0 8px 0;
    font-size: 14px;
    color: var(--text-dim);
  }
  
  p {
    margin: 5px 0;
    font-size: 14px;
    color: white;
  }
  
  @media (max-width: 480px) {
    padding: 8px;
    margin-bottom: 10px;
    
    h4 {
      margin: 0 0 5px 0;
      font-size: 13px;
    }
    
    p {
      font-size: 13px;
      margin: 3px 0;
    }
  }
`;

const EpisodeSuggestionModal = ({ isOpen, onClose, episodeId, episodeTitle, episodeDescription, showTitle, seasonNumber, episodeNumber, episode }) => {
  const [suggestedTitle, setSuggestedTitle] = useState('');
  const [suggestedDescription, setSuggestedDescription] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  
  // Use both episodeTitle and episode.title if available
  const displayTitle = episodeTitle || (episode && episode.title) || `Episode ${episodeNumber}`;
  const displayDescription = episodeDescription || (episode && episode.description) || 'No description available';
  
  // For debugging
  useEffect(() => {
    if (isOpen) {
      console.log("EpisodeSuggestionModal props:", { 
        episodeId, 
        episodeTitle, 
        episodeDescription, 
        showTitle, 
        seasonNumber, 
        episodeNumber,
        episode,
        displayTitle,
        displayDescription
      });
    }
  }, [isOpen, episodeId, episodeTitle, episodeDescription, showTitle, seasonNumber, episodeNumber, episode, displayTitle, displayDescription]);
  
  useEffect(() => {
    // Reset form state when modal opens or closes
    if (isOpen) {
      setSuggestedTitle('');
      setSuggestedDescription('');
      setError('');
      setSuccess(false);
    }
  }, [isOpen]);
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate that at least one field is filled
    if (!suggestedTitle && !suggestedDescription) {
      setError('Please provide either a title or description suggestion');
      return;
    }
    
    try {
      setLoading(true);
      setError('');
      
      const suggestionData = {
        suggested_title: suggestedTitle || undefined,
        suggested_description: suggestedDescription || undefined
      };
      
      await SuggestionsAPI.createEpisodeSuggestion(episodeId, suggestionData);
      setSuccess(true);
      
      // Reset form fields after successful submission
      setSuggestedTitle('');
      setSuggestedDescription('');
      
      // Close modal after 2 seconds on success
      setTimeout(() => {
        onClose();
      }, 2000);
      
    } catch (err) {
      console.error('Error submitting suggestion:', err);
      
      if (err.isRateLimit) {
        setError("You've reached the rate limit. Please wait a moment before trying again.");
      } else {
        const errorMessage = err.response?.data?.message || 'Failed to submit suggestion. Please try again.';
        setError(errorMessage);
      }
    } finally {
      setLoading(false);
    }
  };
  
  if (!isOpen) return null;
  
  return (
    <ModalOverlay onClick={onClose}>
      <ModalContainer onClick={e => e.stopPropagation()}>
        <CloseButton onClick={onClose}>
          <FontAwesomeIcon icon={faTimes} />
        </CloseButton>
        
        <ModalHeader>
          <h2>
            <FontAwesomeIcon icon={faLightbulb} /> Suggest Improvements
          </h2>
        </ModalHeader>
        
        <ModalBody>
          <ContentInfo>
            <p><strong>Show:</strong> {showTitle}</p>
            <p><strong>Episode:</strong> S{seasonNumber} E{episodeNumber}{displayTitle ? ` - ${displayTitle}` : ''}</p>
          </ContentInfo>
          
          <CurrentInfo>
            <h4>Current Information:</h4>
            <p><strong>Title:</strong> {displayTitle}</p>
            <p><strong>Description:</strong> {displayDescription}</p>
          </CurrentInfo>
          
          {success ? (
            <SuccessMessage>
              Thank you for your suggestion! It will be reviewed by our team.
            </SuccessMessage>
          ) : (
            <Form onSubmit={handleSubmit}>
              <FormGroup>
                <Label htmlFor="title">Suggested Title</Label>
                <Input 
                  id="title" 
                  value={suggestedTitle} 
                  onChange={e => setSuggestedTitle(e.target.value)}
                  placeholder="Enter a better title for this episode"
                />
              </FormGroup>
              
              <FormGroup>
                <Label htmlFor="description">Suggested Description</Label>
                <TextArea 
                  id="description" 
                  value={suggestedDescription}
                  onChange={e => setSuggestedDescription(e.target.value)}
                  placeholder="Enter a better description for this episode"
                />
              </FormGroup>
              
              {error && <ErrorMessage>{error}</ErrorMessage>}
              
              <SubmitButton type="submit" disabled={loading || (!suggestedTitle && !suggestedDescription)}>
                {loading ? <FontAwesomeIcon icon={faSpinner} spin /> : "Submit Suggestion"}
              </SubmitButton>
            </Form>
          )}
        </ModalBody>
      </ModalContainer>
    </ModalOverlay>
  );
};

export default EpisodeSuggestionModal; 